// Weight variables with default values
 export const defaultWeights = {
  colorWeight: 0.90,
  tag1Weight: 0.05,  // Tactile Tag Weight
  tag2Weight: 0.25,  // Aesthetic Tag Weight
  tag3Weight: 0.05,  // Technical Tag Weight
  materialWeight: 0.10,
  categoryWeight: 0.30
};

/**
 * Convert hex color to an RGB vector.
 * If no valid color is provided, generate one based on the texture name.
 * 
 * @param {Object} texture - The texture object 
 * @returns {Array} RGB values as an array [r, g, b]
 */
export const getColorVector = (texture) => {
  if (texture.average_color && texture.average_color.startsWith('#')) {
    const r = parseInt(texture.average_color.substr(1, 2), 16);
    const g = parseInt(texture.average_color.substr(3, 2), 16);
    const b = parseInt(texture.average_color.substr(5, 2), 16);
    return [r, g, b];
  }
  let str = texture.texture_name, r = 0, g = 0, b = 0;
  for(let i = 0; i < str.length; i++){
    if(i % 3 === 0) r += str.charCodeAt(i);
    else if(i % 3 === 1) g += str.charCodeAt(i);
    else b += str.charCodeAt(i);
  }
  return [r % 256, g % 256, b % 256];
};

/**
 * Calculate normalized Euclidean distance in RGB space.
 * 
 * @param {Object} texA - First texture object
 * @param {Object} texB - Second texture object
 * @returns {number} Normalized color difference (0-1 scale)
 */
export const calculateColorDifference = (texA, texB) => {
  const vecA = getColorVector(texA);
  const vecB = getColorVector(texB);
  const dist = Math.sqrt(
    Math.pow(vecA[0] - vecB[0], 2) +
    Math.pow(vecA[1] - vecB[1], 2) +
    Math.pow(vecA[2] - vecB[2], 2)
  );
  return dist / 441.67; // Normalize by max possible RGB distance
};

/**
 * Compute Jaccard distance for two tag arrays.
 * 
 * @param {Array} arrA - First array of tags
 * @param {Array} arrB - Second array of tags
 * @returns {number} Jaccard distance between tag sets (0-1)
 */
export const jaccardDistance = (arrA, arrB) => {
  if(!arrA || !arrB || arrA.length === 0 || arrB.length === 0) return 1;
  const setA = new Set(arrA.map(tag => tag.toLowerCase()));
  const setB = new Set(arrB.map(tag => tag.toLowerCase()));
  const intersection = new Set([...setA].filter(x => setB.has(x)));
  const union = new Set([...setA, ...setB]);
  return 1 - (intersection.size / union.size);
};

/**
 * Calculate overall similarity between textures.
 * 
 * @param {Object} selected - Selected texture for comparison
 * @param {Object} candidate - Candidate texture being compared
 * @param {Object} weights - Weight values for different properties
 * @returns {Object} Similarity scores for each property and total
 */
export const calculateSimilarity = (selected, candidate, weights = defaultWeights) => {
  const { 
    colorWeight, 
    tag1Weight, 
    tag2Weight, 
    tag3Weight, 
    materialWeight, 
    categoryWeight 
  } = weights;

  const colorDiff = calculateColorDifference(selected, candidate);
  const tactileDiff = jaccardDistance(selected.tactile_tags, candidate.tactile_tags);
  const aestheticDiff = jaccardDistance(selected.aesthetic_tags, candidate.aesthetic_tags);
  const technicalDiff = jaccardDistance(selected.technical_tags, candidate.technical_tags);
  const materialDiff = (selected.material_name === candidate.material_name) ? 0 : 1;
  const categoryDiff = (selected.category_name === candidate.category_name) ? 0 : 1;
  
  const total = (colorDiff * colorWeight) +
                (tactileDiff * tag1Weight) +
                (aestheticDiff * tag2Weight) +
                (technicalDiff * tag3Weight) +
                (materialDiff * materialWeight) +
                (categoryDiff * categoryWeight);
  
  return {
    color: colorDiff * colorWeight,
    tactile: tactileDiff * tag1Weight,
    aesthetic: aestheticDiff * tag2Weight,
    technical: technicalDiff * tag3Weight,
    material: materialDiff * materialWeight,
    category: categoryDiff * categoryWeight,
    total: total
  };
};

/**
 * Find and sort similar textures.
 * 
 * @param {Array} textures - Array of all texture objects
 * @param {number} selectedIndex - Index of the selected texture
 * @param {number} count - Number of similar textures to return
 * @param {Object} weights - Weight values for different properties
 * @returns {Array} Sorted array of similar textures with scores
 */
export const findSimilarTextures = (textures, selectedIndex, count, weights = defaultWeights) => {
  const selected = textures[selectedIndex];
  let results = [];
  
  textures.forEach((texture, index) => {
    if(index === selectedIndex) return;
    const sim = calculateSimilarity(selected, texture, weights);
    results.push({
      texture: texture,
      color: sim.color,
      tactile: sim.tactile,
      aesthetic: sim.aesthetic,
      technical: sim.technical,
      material: sim.material,
      category: sim.category,
      total: sim.total
    });
  });
  
  results.sort((a, b) => a.total - b.total);
  return results.slice(0, count);
};

export default {
  defaultWeights,
  getColorVector,
  calculateColorDifference,
  jaccardDistance,
  calculateSimilarity,
  findSimilarTextures
}; 